import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { FiSearch } from 'react-icons/fi';

import api from '../../services/api';
import { getProjectCategories } from '../../services/lookups';
import useDebounce from '../../hooks/useDebounce';
import { useFilters } from '../../hooks/projectFilters';

import {
  Box,
  Chip,
  Chips,
  Container,
  SearchButton,
  Suggestion,
  SuggestionImage,
  SuggestionMeta,
  SuggestionName,
  Suggestions,
  SuggestionsEmpty,
} from './styles';

const propTypes = {
  placeholder: PropTypes.string,
  autoFocus: PropTypes.bool,
};

const defaultProps = {
  placeholder: 'Busque por projeto, categoria ou designer...',
  autoFocus: false,
};

const SUGGESTIONS_LIMIT = 5;
const CATEGORIES_LIMIT = 10;

const SearchBox = ({ placeholder, autoFocus }) => {
  const history = useHistory();
  const {
    setSearchString,
    setSelectedCategories,
    setCurrentPage,
    resetFilters,
  } = useFilters();

  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const containerRef = useRef(null);
  const debouncedValue = useDebounce(value, 350);

  // Os chips do redesign são as categorias reais do catálogo — nada estático.
  useEffect(() => {
    (async () => {
      try {
        const data = await getProjectCategories();
        setCategories(data.slice(0, CATEGORIES_LIMIT));
      } catch {
        setCategories([]);
      }
    })();
  }, []);

  useEffect(() => {
    const term = debouncedValue.trim();

    if (term.length < 2) {
      setSuggestions([]);
      setIsSearching(false);
      return undefined;
    }

    let cancelled = false;
    setIsSearching(true);

    (async () => {
      try {
        // Mesma listagem de projetos aprovados usada em /projects.
        const { data } = await api.get('/projects', {
          params: { searchString: term, limit: SUGGESTIONS_LIMIT, page: 1 },
        });
        if (!cancelled) setSuggestions(data.projects);
      } catch {
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedValue]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showSuggestions = useMemo(
    () => isOpen && value.trim().length >= 2,
    [isOpen, value]
  );

  function search(term) {
    resetFilters();
    setSearchString(term);
    setCurrentPage(1);
    setIsOpen(false);
    history.push('/projects');
  }

  function handleKeyDown(event) {
    if (event.key === 'Enter') {
      search(value.trim());
    }
    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  }

  function handleCategoryClick(category) {
    resetFilters();
    // O filtro guarda ids como string — é o que o checkbox da barra lateral
    // produz, e o que mantém o chip e a marcação do filtro consistentes.
    setSelectedCategories([String(category.id)]);
    setCurrentPage(1);
    history.push('/projects');
  }

  function handleSuggestionClick(project) {
    setIsOpen(false);
    history.push(`/projects/${project.id}`);
  }

  return (
    <Container ref={containerRef}>
      <Box>
        <FiSearch size={20} aria-hidden />
        <input
          type="text"
          value={value}
          placeholder={placeholder}
          aria-label="Buscar projetos"
          autoComplete="off"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          onChange={event => {
            setValue(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
        />
        <SearchButton type="button" onClick={() => search(value.trim())}>
          Buscar
        </SearchButton>

        {showSuggestions && (
          <Suggestions role="listbox">
            {suggestions.map(project => (
              <Suggestion
                key={project.id}
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => handleSuggestionClick(project)}
              >
                <SuggestionImage>
                  {project.images && project.images.length > 0 && (
                    <img src={project.images[0].url} alt="" />
                  )}
                </SuggestionImage>
                <div>
                  <SuggestionName>{project.name}</SuggestionName>
                  <SuggestionMeta>
                    {project.designerUser?.name}
                    {project.categories?.length
                      ? ` · ${project.categories[0].name}`
                      : ''}
                  </SuggestionMeta>
                </div>
              </Suggestion>
            ))}

            {!suggestions.length && (
              <SuggestionsEmpty>
                {isSearching
                  ? 'Buscando projetos...'
                  : 'Nenhum projeto encontrado'}
              </SuggestionsEmpty>
            )}

            {!!suggestions.length && (
              <Suggestion
                type="button"
                onClick={() => search(value.trim())}
                as="button"
              >
                <SuggestionName>
                  Ver todos os resultados para “{value.trim()}”
                </SuggestionName>
              </Suggestion>
            )}
          </Suggestions>
        )}
      </Box>

      {categories.length > 0 && (
        <Chips>
          {categories.map(category => (
            <Chip
              key={category.id}
              type="button"
              onClick={() => handleCategoryClick(category)}
            >
              {category.name}
            </Chip>
          ))}
        </Chips>
      )}
    </Container>
  );
};

SearchBox.propTypes = propTypes;
SearchBox.defaultProps = defaultProps;

export default SearchBox;
